import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Download, Loader2, Plus } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { usePermission } from "@/hooks/usePermission"
import { useSponsorPortfolio } from "@/hooks/useSponsorships"
import { useSponsorSubscription } from "@/hooks/useSponsorSubscription"
import { usePortfolioFunds } from "@/hooks/useFundLedger"
import { SponsorSubscriptionGate } from "@/components/sponsor/SponsorSubscriptionGate"
import { SponsorStatCards } from "@/components/sponsor/SponsorStatCards"
import { SponsorProjectTable } from "@/components/sponsor/SponsorProjectTable"
import { CreateChildOrgModal } from "@/components/sponsor/CreateChildOrgModal"
import {
  buildSponsorDonationsCsv,
  downloadCsv,
  type ProjectTableRow,
} from "@/lib/sponsor"
import {
  buildPortfolioFundCsv,
  buildScheduleICsv,
  summarizeProject,
  sumEntries,
} from "@/lib/fundLedger"
import { supabase } from "@/lib/supabase"

export function SponsorDashboardPage() {
  const navigate = useNavigate()
  const { hasPermission, loading: permLoading } = usePermission("can_view_child_financials")
  const { hasPermission: canManageChildOrgs } = usePermission("can_manage_child_orgs")
  const portfolio = useSponsorPortfolio()
  const subscription = useSponsorSubscription()
  const [createOpen, setCreateOpen] = useState(false)

  // NOS-196: the front door is the permission alone (owners/admins always hold
  // it — has_permission returns true for every key for those roles). Requiring
  // an existing sponsorship here was a dead-end: the subscription checkout
  // renders inside this page, but a brand-new sponsor org has zero sponsorships
  // and bounced before ever seeing it (and creating the first child is gated on
  // the subscription). Members without sponsor permissions still redirect.
  const gateLoading = permLoading || portfolio.loading || subscription.loading
  const allowed = hasPermission

  useEffect(() => {
    if (!gateLoading && !allowed) {
      navigate("/dashboard", { replace: true })
    }
  }, [gateLoading, allowed, navigate])

  const childIds = useMemo(
    () => portfolio.sponsorships.map((s) => s.child_org_id),
    [portfolio.sponsorships]
  )
  const funds = usePortfolioFunds(childIds)

  // Pending funds requests per child — badge in the project table (NOS-171)
  const [pendingByChild, setPendingByChild] = useState<Map<string, number>>(new Map())
  useEffect(() => {
    let cancelled = false
    async function fetchPending() {
      if (childIds.length === 0) return
      const { data } = await supabase
        .from("disbursement_requests")
        .select("child_org_id")
        .in("child_org_id", childIds)
        .eq("status", "pending")
        .limit(1000)
      if (cancelled) return
      const counts = new Map<string, number>()
      for (const row of data ?? []) {
        counts.set(row.child_org_id, (counts.get(row.child_org_id) ?? 0) + 1)
      }
      setPendingByChild(counts)
    }
    fetchPending()
    return () => {
      cancelled = true
    }
  }, [childIds])

  const tableRows: ProjectTableRow[] = useMemo(
    () =>
      portfolio.sponsorships.map((s) => {
        const stats = portfolio.rollup.byOrg.get(s.child_org_id)
        const fundEntries = funds.entriesByChild.get(s.child_org_id)
        return {
          sponsorship: s,
          ytdCents: stats?.ytdCents ?? 0,
          totalCents: stats?.totalCents ?? 0,
          lastDonationAt: stats?.lastDonationAt ?? null,
          memberCount: portfolio.memberCountsByOrg.get(s.child_org_id) ?? 0,
          contactCount: portfolio.contactCountsByOrg.get(s.child_org_id) ?? 0,
          fundBalanceCents:
            s.mode === "fiscal" ? sumEntries(fundEntries ?? []) : null,
          pendingRequests: pendingByChild.get(s.child_org_id) ?? 0,
        }
      }),
    [portfolio.sponsorships, portfolio.rollup, portfolio.memberCountsByOrg, portfolio.contactCountsByOrg, funds.entriesByChild, pendingByChild]
  )

  const handleExport = () => {
    if (portfolio.donations.length === 0) {
      toast.info("No donations to export yet.")
      return
    }
    const orgNames = new Map(portfolio.sponsorships.map((s) => [s.child_org_id, s.child.name]))
    const csv = buildSponsorDonationsCsv(portfolio.donations, orgNames, portfolio.campaignTitlesById)
    downloadCsv(csv, `sponsor-donations-${new Date().toISOString().split("T")[0]}.csv`)
    toast.success("Donation export downloaded.")
  }

  // Fund summaries need the children's legal identity (Schedule I shape) —
  // fetched on demand at export time, not on page load.
  const buildFundSummaries = async () => {
    const { data: identities } = await supabase
      .from("organizations")
      .select("id, legal_name, ein, mailing_address")
      .in("id", childIds)
    const identityById = new Map((identities ?? []).map((o) => [o.id as string, o]))
    return portfolio.sponsorships
      .filter((s) => s.mode === "fiscal")
      .map((s) => {
        const identity = identityById.get(s.child_org_id)
        return summarizeProject(funds.entriesByChild.get(s.child_org_id) ?? [], {
          childName: s.child.name,
          childLegalName: (identity?.legal_name as string | null) ?? null,
          childEin: (identity?.ein as string | null) ?? null,
          childAddress: (identity?.mailing_address as string | null) ?? null,
        })
      })
  }

  const handleFundExport = async () => {
    const summaries = await buildFundSummaries()
    if (summaries.length === 0) {
      toast.info("No fiscal projects with fund activity yet.")
      return
    }
    downloadCsv(
      buildPortfolioFundCsv(summaries),
      `fund-report-${new Date().toISOString().split("T")[0]}.csv`
    )
    toast.success("Fund report downloaded.")
  }

  const handleScheduleIExport = async () => {
    const summaries = await buildFundSummaries()
    const csv = buildScheduleICsv(summaries)
    if (csv.split("\n").length <= 1) {
      toast.info("No disbursements this year — Schedule I export is empty.")
      return
    }
    downloadCsv(csv, `schedule-i-${new Date().getFullYear()}.csv`)
    toast.success("Schedule I export downloaded.")
  }

  if (gateLoading) {
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center gap-4">
        <Loader2 className="h-10 w-10 animate-spin text-primary" aria-hidden />
        <p className="text-muted-foreground">Loading sponsor console...</p>
      </div>
    )
  }

  if (!allowed) return null

  // NOS-170: subscription gate. Children are never affected — this only
  // covers the sponsor console itself. Resolves BEFORE any sponsorship
  // requirement so a new sponsor can subscribe (NOS-196); gateLoading above
  // already waited for subscription.loading.
  if (!subscription.canAccess) {
    return <SponsorSubscriptionGate subscription={subscription} />
  }

  const activeProjects = portfolio.sponsorships.filter(
    (s) => s.status === "active" || s.status === "transferring"
  ).length
  const pendingInvites = portfolio.sponsorships.filter((s) => s.status === "invited").length

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold">Sponsor console</h1>
          <p className="text-sm text-muted-foreground">
            Portfolio across {portfolio.sponsorships.length}{" "}
            {portfolio.sponsorships.length === 1 ? "sponsored project" : "sponsored projects"}
          </p>
        </div>
        <div className="flex gap-2">
          {canManageChildOrgs && subscription.canWrite && (
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Plus className="mr-2 h-4 w-4" aria-hidden />
              Add organization
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="mr-2 h-4 w-4" aria-hidden />
            Donations CSV
          </Button>
          <Button variant="outline" size="sm" onClick={handleFundExport}>
            <Download className="mr-2 h-4 w-4" aria-hidden />
            Fund report CSV
          </Button>
          <Button variant="outline" size="sm" onClick={handleScheduleIExport}>
            <Download className="mr-2 h-4 w-4" aria-hidden />
            Schedule I CSV
          </Button>
        </div>
      </div>

      <CreateChildOrgModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={portfolio.refresh}
      />

      {subscription.status === "grace" && !subscription.manualActive && (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Your sponsor subscription payment failed — the console is read-only until payment
          recovers
          {subscription.graceExpiresAt
            ? ` (grace ends ${new Date(subscription.graceExpiresAt).toLocaleDateString()})`
            : ""}
          . Your sponsored projects are unaffected.
        </p>
      )}

      {portfolio.error && (
        <p className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {portfolio.error}
        </p>
      )}

      <SponsorStatCards
        stats={{
          totalRaisedCents: portfolio.rollup.totalRaisedCents,
          raisedThisMonthCents: portfolio.rollup.raisedThisMonthCents,
          activeProjects,
          pendingInvites,
        }}
      />

      {portfolio.sponsorships.length === 0 ? (
        <div className="rounded-lg border border-dashed p-12 text-center">
          <p className="font-medium">No sponsored projects yet</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {canManageChildOrgs && subscription.canWrite
              ? "Your sponsor suite is active. Create your first organization to start tracking its fundraising here."
              : "Projects you sponsor will appear here with their fundraising activity."}
          </p>
          {canManageChildOrgs && subscription.canWrite && (
            <Button className="mt-4" onClick={() => setCreateOpen(true)}>
              <Plus className="mr-2 h-4 w-4" aria-hidden />
              Create your first organization
            </Button>
          )}
        </div>
      ) : (
        <SponsorProjectTable rows={tableRows} />
      )}
    </div>
  )
}
